import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { AiOutlineSearch } from 'react-icons/ai';
import SummaryApi from '../common';
import { useTable } from 'react-table';
import Modal from 'react-modal';

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
};

const SearchStudents = () => {
  const [studentsData, setStudentsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [memberType, setMemberType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  const fetchMemberType = () => {
    const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
    const memberType = encodedMemberType ? atob(encodedMemberType) : null;
    setMemberType(memberType);
    return memberType;
  };

  useEffect(() => {
    fetchMemberType();
  }, []);

  const fetchStudents = async (searchTerm, memberType) => {
    setLoading(true);
    try {
      let response;
      if (memberType === 'Admin' || memberType === 'Manager') {
        response = await fetch(SummaryApi.searchstudents.url, {
          method: SummaryApi.searchstudents.method,
          headers,
          body: JSON.stringify({ searchterm: searchTerm }),
        });
      } else {
        const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
        if (!encodedEmployeeId) {
          throw new Error('Employee ID not found in session storage');
        }
        const decodedEmployeeId = atob(encodedEmployeeId);

        response = await fetch(SummaryApi.searchstudentsById.url, {
          method: SummaryApi.searchstudentsById.method,
          headers,
          body: JSON.stringify({ employee_id: decodedEmployeeId, searchterm: searchTerm }),
        });
      }

      if (!response.ok) {
        throw new Error('Failed to fetch data from the endpoint');
      }

      const data = await response.json();
      const students = data?.data || [];
      const updatedStudentsData = students.map((student, index) => ({
        ...student,
        sno: index + 1,
      }));

      setStudentsData(updatedStudentsData);
      setFilteredData(updatedStudentsData);
    } catch (error) {
      toast.error(`Error fetching students: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (term.length >= 3) {
      fetchStudents(term, memberType);
    } else {
      setFilteredData([]);
    }
  };

  const handleViewMore = (student) => {
    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) {
      return 'NA';
    }
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'SNO',
        Cell: ({ row }) => row.index + 1,
      },
      ...(['Admin', 'Manager'].includes(memberType) ? [
        {
          Header: 'Employee Name',
          accessor: 'employee_name',
        },
      ] : []),
      {
        Header: 'Enrollment Code',
        accessor: 'enrollment_code',
      },
      {
        Header: 'Student Name',
        accessor: 'student_name',
      },
      {
        Header: 'Mobile Number',
        accessor: 'mobile_number1',
      },
      {
        Header: 'Aadhar Number',
        accessor: 'aadhar_number',
      },
      {
        Header: 'Enrolled Date',
        accessor: 'enroll_date',
        Cell: ({ row }) => {
          const { created_date } = row.original;
          return <span>{formatDate(created_date)}</span>;
        },
      },
      {
        Header: 'Status',
        accessor: 'is_interested',
        Cell: ({ value }) => {
          let colorClass = 'text-gray-600';
          if (value === 'Most Interested') {
            colorClass = 'text-green-600';
          } else if (value === 'Interested') {
            colorClass = 'text-yellow-500';
          } else if (value === 'Not Interested') {
            colorClass = 'text-red-600';
          }
          return (
            <span className={`font-semibold ${colorClass}`}>
              {value}
            </span>
          );
        },
      },
      {
        Header: 'Action',
        accessor: 'action',
        Cell: ({ row }) => (
          <button
            onClick={() => handleViewMore(row.original)}
            style={{ color: THEME.actionBlue }}
            onMouseEnter={(e) => (e.currentTarget.style.color = THEME.actionBlueHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = THEME.actionBlue)}
            className="font-semibold hover:underline whitespace-nowrap transition-colors"
          >
            View More
          </button>
        ),
      },
    ],
    [memberType]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: filteredData,
  });

  Modal.setAppElement('#root');

  return (
    <div className="container mx-auto">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
          <span className="text-white text-lg">Fetching Data...</span>
        </div>
      )}
      <div className="w-full">
        <h1
          className="text-3xl font-bold text-center mb-6"
          style={{ color: THEME.primary }}
        >
          Search Students
        </h1>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <AiOutlineSearch className="text-gray-400 text-xl" />
            </div>
            <input
              type="text"
              placeholder={
                ['Admin', 'Manager'].includes(memberType)
                  ? "Search by student name, mobile number, or employee name..."
                  : "Search by student name or mobile number..."
              }
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] shadow-sm"
            />
          </div>
          {searchTerm.length > 0 && searchTerm.length < 3 && (
            <p className="text-xs text-gray-500 mt-2 text-center">
              Please enter at least 3 characters to search.
            </p>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table {...getTableProps()} className="min-w-full">
            <thead className=" bg-gray-200">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps()}
                      className="px-4 py-3 text-left font-semibold whitespace-nowrap"
                    >
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className="divide-y divide-gray-100 bg-white">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-10 text-gray-500">
                    {searchTerm.length < 3
                      ? "Please enter at least 3 characters to search."
                      : `No records found for "${searchTerm}".`}
                  </td>
                </tr>
              ) : (
                rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="hover:bg-slate-50 transition duration-200"
                    >
                      {row.cells.map(cell => (
                        <td {...cell.getCellProps()} className="px-4 py-3 text-sm text-gray-700">
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Student Details Modal */}
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          contentLabel="Student Details"
          className="w-full max-w-2xl mx-auto my-10 mt-[100px] bg-white rounded-xl shadow-lg overflow-hidden outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start overflow-y-auto z-50"
        >
          {/* Sticky Header */}
          <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <h2
              className="text-lg font-bold"
              style={{ color: THEME.primary }}
            >
              Student Details
            </h2>
            <button
              onClick={closeModal}
              className="text-gray-500 hover:text-red-500 text-2xl leading-none transition-colors"
            >
              &times;
            </button>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {selectedStudent && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                <DetailRow label="Enrollment Code" value={selectedStudent.enrollment_code} />
                <DetailRow label="Student Name" value={selectedStudent.student_name} highlight />
                <DetailRow label="Gender" value={selectedStudent.gender} />
                <DetailRow label="Father's Name" value={selectedStudent.father_name} />
                <DetailRow label="Mobile Number 1" value={selectedStudent.mobile_number1} />
                <DetailRow label="Mobile Number 2" value={selectedStudent.mobile_number2 || 'N/A'} />
                <DetailRow label="Email" value={selectedStudent.email} />
                <DetailRow label="Aadhar Number" value={selectedStudent.aadhar_number} />
                <DetailRow label="State" value={selectedStudent.state} />
                <DetailRow label="District" value={selectedStudent.district} />
                <DetailRow label="City" value={selectedStudent.city} />
                <DetailRow label="Pincode" value={selectedStudent.pincode} />
                <DetailRow label="Course" value={selectedStudent.course} />
                <DetailRow label="College" value={selectedStudent.clg_name} />
                <DetailRow label="Intermediate Percentage/CGPA" value={selectedStudent.inter_percentage} />
                <DetailRow label="Program Type" value={selectedStudent.program_type} />
                <DetailRow label="UG/PG Course" value={selectedStudent.ug_pg_course} />
                <DetailRow label="Call Status" value={selectedStudent.call_status} />

                <div className="col-span-1 sm:col-span-2">
                  <DetailRow label="Remarks" value={selectedStudent.remarks} />
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <span
                    className="block text-xs font-semibold mb-1"
                    style={{ color: THEME.primary }}
                  >
                    Status
                  </span>
                  <span
                    className={`font-semibold ${selectedStudent.is_interested === "Most Interested"
                      ? 'text-green-600'
                      : selectedStudent.is_interested === "Interested"
                        ? 'text-yellow-500'
                        : selectedStudent.is_interested === "Not Interested"
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}
                  >
                    {selectedStudent.is_interested || 'N/A'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────
// Small presentational helper for detail rows
// ────────────────────────────────────────────────
const DetailRow = ({ label, value, highlight = false }) => (
  <div className="flex flex-col">
    <span
      className="text-xs font-semibold mb-0.5"
      style={{ color: THEME.primary }}
    >
      {label}
    </span>
    <span className={`text-sm ${highlight ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
      {value || 'N/A'}
    </span>
  </div>
);

export default SearchStudents;